import { useRef, useEffect, useCallback, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "./context-menu";
import { Button } from "./button";
import { EmptyState } from "./empty-state";
import { TableSkeleton } from "./skeleton";
import { PresenceAvatars } from "./presence-avatars";
import { MoreVertical, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "../../lib/utils";
import type { LucideIcon } from "lucide-react";
import type { PresenceMap } from "../../types/data-table";

export interface RowAction<TData> {
  label: string;
  icon: LucideIcon;
  onClick: (row: TData) => void;
  visible?: (row: TData) => boolean;
  variant?: "default" | "destructive";
  separatorBefore?: boolean;
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  getRowId: (row: TData) => string;
  isLoading?: boolean;
  emptyIcon?: LucideIcon;
  emptyTitle?: string;
  emptyDescription?: string;
  presence?: PresenceMap;
  presenceKey?: (row: TData) => number;
  currentInstanceId?: string;
  actions?: RowAction<TData>[];
  onRowClick?: (row: TData) => void;
  onVisibleRowsChange?: (rows: TData[]) => void;
  virtualize?: boolean;
  height?: string;
  initialSorting?: SortingState;
}

const ROW_HEIGHT = 44;
const OVERSCAN = 10;

export function DataTable<TData>({
  columns,
  data,
  getRowId,
  isLoading = false,
  emptyIcon,
  emptyTitle = "No data",
  emptyDescription,
  presence,
  presenceKey,
  currentInstanceId,
  actions,
  onRowClick,
  onVisibleRowsChange,
  virtualize = true,
  height = "calc(100vh - 280px)",
  initialSorting = [],
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const scrollRef = useRef<HTMLDivElement>(null);
  const onVisibleRowsChangeRef = useRef(onVisibleRowsChange);
  onVisibleRowsChangeRef.current = onVisibleRowsChange;

  const columnVisibility = useMemo<VisibilityState>(() => {
    if (data.length === 0) return {};
    const vis: VisibilityState = {};
    for (const col of columns) {
      const key = (col as any).accessorKey as string | undefined;
      if (!key) continue;
      const allMissing = data.every((row) => (row as any)[key] === undefined);
      if (allMissing) vis[key] = false;
    }
    return vis;
  }, [data, columns]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId,
  });

  const { rows } = table.getRowModel();

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
    enabled: virtualize,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    if (!onVisibleRowsChangeRef.current || !virtualize) return;
    const visibleData = virtualItems.map((vi) => rows[vi.index]!.original);
    onVisibleRowsChangeRef.current(visibleData);
  }, [virtualItems, rows, virtualize]);

  const getVisibleActions = useCallback(
    (row: TData) =>
      actions?.filter((a) => !a.visible || a.visible(row)) ?? [],
    [actions]
  );

  const renderMenuItems = useCallback(
    (
      row: TData,
      Item: typeof DropdownMenuItem | typeof ContextMenuItem,
      Sep: typeof DropdownMenuSeparator | typeof ContextMenuSeparator
    ) => {
      const visible = getVisibleActions(row);
      return visible.map((action, i) => (
        <span key={action.label}>
          {action.separatorBefore && i > 0 && <Sep />}
          <Item
            onClick={(e) => {
              e.stopPropagation();
              action.onClick(row);
            }}
            className={
              action.variant === "destructive"
                ? "text-destructive focus:text-destructive"
                : undefined
            }
          >
            <action.icon className="mr-2 h-4 w-4" />
            {action.label}
          </Item>
        </span>
      ));
    },
    [getVisibleActions]
  );

  if (isLoading) return <TableSkeleton rows={5} />;

  if (data.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }

  const headerGroups = table.getHeaderGroups();
  const hasActions = actions && actions.length > 0;
  const hasPresence = presence && presenceKey;

  return (
    <div className="rounded-md border bg-card">
      <div
        ref={scrollRef}
        style={{ height: virtualize ? height : undefined, overflow: virtualize ? "auto" : undefined }}
      >
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card">
            {headerGroups.map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {hasPresence && (
                  <TableHead className="w-0 p-0">
                    <span className="sr-only">Viewers</span>
                  </TableHead>
                )}
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as any;
                  const hideBelow = meta?.hideBelow;
                  const headerClassName = cn(
                    hideBelow === "md" && "hidden md:table-cell",
                    hideBelow === "lg" && "hidden lg:table-cell"
                  );

                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        headerClassName,
                        header.column.getCanSort() && "cursor-pointer select-none"
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-1">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {header.column.getIsSorted() === "asc" && (
                          <ArrowUp className="h-3 w-3" />
                        )}
                        {header.column.getIsSorted() === "desc" && (
                          <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </TableHead>
                  );
                })}
                {hasActions && (
                  <TableHead className="w-[50px]">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                )}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {virtualize && virtualItems.length > 0 && (
              <tr style={{ height: virtualItems[0]!.start }} />
            )}
            {(virtualize ? virtualItems : rows.map((r, i) => ({ index: i }))).map(
              (virtualRow) => {
                const row = rows[virtualRow.index]!;
                const rowData = row.original;
                const presenceViewers =
                  hasPresence ? presence[presenceKey(rowData)] : undefined;

                const rowContent = (
                  <TableRow
                    key={row.id}
                    className={cn(onRowClick && "cursor-pointer")}
                    onClick={() => onRowClick?.(rowData)}
                    style={virtualize ? { height: ROW_HEIGHT } : undefined}
                  >
                    {hasPresence && (
                      <TableCell className="w-0 p-0">
                        <div className="overflow-visible whitespace-nowrap px-2">
                          {presenceViewers && presenceViewers.length > 0 && (
                            <PresenceAvatars
                              viewers={presenceViewers}
                              currentInstanceId={currentInstanceId}
                            />
                          )}
                        </div>
                      </TableCell>
                    )}
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta as any;
                      const hideBelow = meta?.hideBelow;
                      const cellClassName = cn(
                        hideBelow === "md" && "hidden md:table-cell",
                        hideBelow === "lg" && "hidden lg:table-cell"
                      );

                      return (
                        <TableCell key={cell.id} className={cellClassName}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                    {hasActions && (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            asChild
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {renderMenuItems(
                              rowData,
                              DropdownMenuItem,
                              DropdownMenuSeparator
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );

                if (!hasActions) return rowContent;

                return (
                  <ContextMenu key={row.id}>
                    <ContextMenuTrigger asChild>{rowContent}</ContextMenuTrigger>
                    <ContextMenuContent>
                      {renderMenuItems(
                        rowData,
                        ContextMenuItem,
                        ContextMenuSeparator
                      )}
                    </ContextMenuContent>
                  </ContextMenu>
                );
              }
            )}
            {virtualize && virtualItems.length > 0 && (
              <tr
                style={{
                  height:
                    virtualizer.getTotalSize() -
                    virtualItems[virtualItems.length - 1]!.end,
                }}
              />
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
